import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import useAddress from "./useAddress";

const useOrders = (userId, status, initialState = {}) => {
  const { addresses } = useAddress(userId);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [success, setSuccess] = useState(false);

  // States from OrderSummary
  const [address, setAddress] = useState(initialState.selectedAddress || null);
  const [products, setProducts] = useState(initialState.selectedProducts || []);
  const [totalPrice, setTotalPrice] = useState(initialState.totalPrice || 0);
  const [orderId] = useState(initialState.orderId || null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!userId || !status) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get("https://inaaniserver-50c4305cc7f7.herokuapp.com/order/by_status", {
        params: { user_id: userId, status },
      });
      console.log('fetchOrders response:', response.data); // Debug

      if (!response.data.error && Array.isArray(response.data.data)) {
        const formattedOrders = response.data.data.map((order) => {
          const productEntries = Object.values(order).filter(
            (item) => typeof item === "object" && item.product_id
          );

          const userAddress = addresses.find((address) => address.user_id === userId);

          return {
            orderId: order.order_id || `temp-${Math.random()}`,
            products: productEntries.map((product) => ({
              id: product.product_id,
              product_name: product.product_name || "No Name",
              quantity: product.quantity || 1,
              price: parseFloat(product.price) || 0.0,
              image: product.image
                ? `data:image/jpeg;base64,${product.image}`
                : "https://via.placeholder.com/150",
            })),
            status: order.status || (productEntries.length > 0 ? productEntries[0].status : "unknown"),
            address: userAddress ? userAddress.address : order.address || "No Address",
          };
        });

        setOrders(formattedOrders);
      } else {
        throw new Error(response.data.message || "Invalid API response");
      }
    } catch (err) {
      console.error("fetchOrders error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, status, addresses]);

  // Fetch order ID
  const fetchOrderId = useCallback(async (productId, userId) => {
    try {
      const response = await axios.get("https://inaaniserver-50c4305cc7f7.herokuapp.com/order/getOrderId", {
        params: { product_id: productId, user_id: userId },
      });
      console.log(`fetchOrderId response: ${response.data.orderId} for product ${productId}`); // Debug
      if (!response.data.error && response.data.orderId) {
        return response.data.orderId;
      }
      throw new Error("Order ID not found");
    } catch (err) {
      console.error("fetchOrderId error:", err);
      return null;
    }
  }, []);

  // Update order
  const updateOrder = useCallback(async (orderData) => {
    setUpdateLoading(true);
    setUpdateError(null);
    setSuccess(false);

    try {
      console.log("updateOrder payload:", orderData); // Debug
      const response = await axios.post("https://inaaniserver-50c4305cc7f7.herokuapp.com/order/update", orderData);
      console.log("updateOrder response:", response.data); // Debug

      if (response.data.error) {
        throw new Error(response.data.message || "Failed to update order");
      }

      setSuccess(true);
      await fetchOrders(); // Explicitly re-fetch orders
      return response.data;
    } catch (err) {
      console.error("updateOrder error:", err);
      setUpdateError(err.message);
      throw err; // Rethrow for caller
    } finally {
      setUpdateLoading(false);
    }
  }, [fetchOrders]);

  // Handle quantity change (from OrderSummary)
  const handleQuantityChange = useCallback((index, action, customValue) => {
    setProducts((prevProducts) => {
      const updatedProducts = prevProducts.map((product, i) => {
        if (i === index) {
          let newQuantity = product.quantity;
          if (action === "increment") newQuantity += 1;
          else if (action === "decrement" && newQuantity > 1) newQuantity -= 1;
          else if (action === "custom") newQuantity = customValue > 0 ? customValue : 1;

          return { ...product, quantity: newQuantity };
        }
        return product;
      });

      const newTotalPrice = updatedProducts.reduce(
        (sum, product) => sum + product.price * product.quantity,
        0
      );

      setTotalPrice(newTotalPrice);
      return updatedProducts;
    });
  }, []);

  // Handle order submission (from OrderSummary)
  const handleSubmitOrder = useCallback(
    async (navigate) => {
      if (!address) {
        setAlertMessage("Please provide a shipping address!");
        return;
      }

      if (products.length === 0) {
        setAlertMessage("No products selected. Please add items to the cart.");
        return;
      }

      const requiredFields = {
        "contact.name": address.contact?.name,
        "contact.phone": address.contact?.phone,
        "location.home": address.location?.home,
        "location.municipality": address.location?.municipality,
      };
      const missingFields = Object.keys(requiredFields).filter(
        (key) => !requiredFields[key]
      );

      if (missingFields.length > 0) {
        setAlertMessage(
          `Please provide the following address details: ${missingFields
            .map((field) => field.split(".")[1])
            .join(", ")}`
        );
        return;
      }

      try {
        const responses = await Promise.all(
          products.map(async (product) => {
            let currentOrderId = orderId || (await fetchOrderId(product.id, userId));

            if (!currentOrderId) return null;

            const orderData = {
              id: currentOrderId,
              product_id: product.id,
              user_id: userId,
              quantity: product.quantity,
              price: product.price,
              total_amount: product.price * product.quantity,
              name: address.contact.name,
              contact_number: address.contact.phone,
              municipality: address.location.municipality,
              home_address: address.location.home,
              address: `${address.contact.name}, ${address.contact.phone}, ${address.location.home}, ${address.location.barangay || ""}, ${address.location.municipality}`,
              status: "to pay",
            };

            return updateOrder(orderData);
          })
        );

        const allSuccessful = responses.every((response) => response && !response.error);

        if (allSuccessful) {
          setIsSuccessModalOpen(true);
          setTimeout(() => {
            setIsSuccessModalOpen(false);
            navigate("/order");
          }, 1500);
        } else {
          setAlertMessage("⚠️ Some orders failed to update. Please try again.");
        }
      } catch (error) {
        console.error("handleSubmitOrder error:", error);
        setAlertMessage("An error occurred while updating your order. Please try again.");
      }
    },
    [address, products, orderId, userId, fetchOrderId, updateOrder]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, success]);

  return {
    orders,
    loading,
    error,
    updateOrder,
    fetchOrderId,
    updateLoading,
    updateError,
    success,
    address,
    setAddress,
    products,
    setProducts,
    totalPrice,
    setTotalPrice,
    orderId,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    alertMessage,
    setAlertMessage,
    handleQuantityChange,
    handleSubmitOrder,
  };
};

export default useOrders;